import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import bytes from "bytes";
import { hideModal, showEditAssetModal } from "actions/modal";
import { updateCollectionAsset } from "actions/mediaLibrary";
import { getUser } from "selectors/commonSelectors";
import { isLibraryRestricted } from "features/media-library";
import { useGridMediaCard } from "hooks/useGridMediaCard";
import type { AssetTagChanges, CollectionFile } from "shared/types/files";
import { checkIfHeic } from "utils/image";
import { entitlementsPermission } from "utils/entitlements-permission";
import { convertCustomerUploadsS3ToCloudFront } from "libs/media-files";
import CreatePostIcon from "assets/icons/library-create-post.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ContentCollectProvenancePanel } from "../content-collect";
import { useAssetVision } from "./useAssetVision";

const CreativePhotoEditorModal = lazy(() =>
  import("ui/photo-editor").then((m) => ({
    default: m.CreativePhotoEditorModal,
  }))
);
const CreativeVideoEditorModal = lazy(() =>
  import("ui/video-editor").then((m) => ({
    default: m.CreativeVideoEditorModal,
  }))
);
const labelClass =
  "block font-inter text-sm font-medium leading-5 text-neutral-800";

type AssetDetailsProps = {
  asset: CollectionFile;
  isReadOnly?: boolean;
  loading?: boolean;
  requestedAction?: (() => void) | null;
};

export default function AssetDetailsSheet(props: AssetDetailsProps) {
  const [displayed, setDisplayed] = useState(props);
  useLayoutEffect(() => {
    const library = document.querySelector(".MediaLibrary");
    if (!library) return;
    const style = document.documentElement.style;
    const properties = [
      "--library-details-top",
      "--library-details-right",
      "--library-details-bottom",
    ];
    const previousValues = properties.map((property) =>
      style.getPropertyValue(property)
    );
    const measure = () => {
      const rect = library.getBoundingClientRect();
      [
        rect.top,
        window.innerWidth - rect.right,
        window.innerHeight - rect.bottom,
      ].forEach((value, index) =>
        style.setProperty(properties[index], `${value}px`)
      );
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(library);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      properties.forEach((property, index) =>
        previousValues[index]
          ? style.setProperty(property, previousValues[index])
          : style.removeProperty(property)
      );
    };
  }, []);
  return (
    <AssetDetailsForm
      key={`${displayed.asset.asset_id}:${displayed.asset._id}`}
      {...displayed}
      loading={props.loading}
      requestedAsset={props.asset}
      requestedAction={props.requestedAction}
      onSwitchAsset={() => setDisplayed(props)}
    />
  );
}

function AssetDetailsForm({
  asset,
  isReadOnly = false,
  loading = false,
  requestedAsset,
  requestedAction,
  onSwitchAsset,
}: AssetDetailsProps & {
  requestedAsset: CollectionFile;
  onSwitchAsset: () => void;
}) {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  // Keep usage/favorite information current while the form retains its own edits.
  const currentAsset = useSelector(
    (state: {
      entities: { collectionEntries: { byId: Record<string, CollectionFile> } };
    }) => state.entities.collectionEntries.byId[asset.asset_id]
  ) as CollectionFile | undefined;
  const liveAsset = currentAsset?._id === asset._id ? currentAsset : asset;
  const card = useGridMediaCard(liveAsset);
  const readOnly =
    isReadOnly ||
    card.isManagedCollection ||
    Boolean(
      asset.frontifyMeta?.frontifyAssetId || asset.externalAssetRef?.provider
    );
  const canTag = !readOnly && !isLibraryRestricted(user);
  const [name, setName] = useState(asset.name ?? "");
  const [description, setDescription] = useState(asset.description ?? "");
  const {
    vision,
    pending: visionPending,
    error: visionError,
    analyze,
    reload,
  } = useAssetVision(liveAsset);
  // Store user intent separately so server additions are never dirty form edits.
  const [tagChanges, setTagChanges] = useState<AssetTagChanges>({
    add: [],
    remove: [],
  });
  const serverTags = liveAsset.tags ?? [];
  const tags = [...new Set([...serverTags, ...tagChanges.add])].filter(
    (tag) => !tagChanges.remove.includes(tag)
  );
  const changesForTags = (nextTags: string[]): AssetTagChanges => ({
    add: nextTags.filter((tag) => !serverTags.includes(tag)),
    // Retain deliberate removals even if a subsequent server snapshot omits them.
    remove: [...new Set([...tagChanges.remove, ...serverTags])].filter(
      (tag) => !nextTags.includes(tag)
    ),
  });
  const setTags = (nextTags: string[]) =>
    setTagChanges(changesForTags(nextTags));
  const [tagInput, setTagInput] = useState("");
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const dirty =
    name !== (asset.name ?? "") ||
    description !== (asset.description ?? "") ||
    tagChanges.add.length > 0 ||
    tagChanges.remove.length > 0 ||
    Boolean(tagInput.trim());
  const editorOpen = card.showPhotoEditor || card.showVideoEditor;
  const close = useCallback(
    () => dispatch(hideModal(false, "EDIT_COLLECTION_ASSET")),
    [dispatch]
  );
  const requestLeave = useCallback(
    (action: () => void = close) => {
      if (loading || editorOpen || pendingAction) return;
      if (dirty && !readOnly) setPendingAction(() => action);
      else action();
    },
    [close, loading, editorOpen, pendingAction, dirty, readOnly]
  );
  // A new Redux request must not replace the form until its leave guard allows it.
  useEffect(() => {
    if (requestedAsset._id !== asset._id) requestLeave(onSwitchAsset);
  }, [asset._id, requestedAsset._id, onSwitchAsset, requestLeave]);
  useEffect(() => {
    if (requestedAction)
      requestLeave(() => {
        close();
        requestedAction();
      });
  }, [requestedAction, requestLeave, close]);
  const keepEditing = () => {
    // Restore the current request so the same other card can be requested again.
    if (requestedAsset._id !== asset._id || requestedAction) {
      dispatch(hideModal(false, "EDIT_COLLECTION_ASSET"));
      dispatch(showEditAssetModal(asset, isReadOnly));
    }
    setPendingAction(null);
  };
  const withPendingTag = () => {
    const tag = tagInput.trim();
    return tag && !tags.includes(tag) ? [...tags, tag] : tags;
  };
  const addTag = () => {
    setTags(withPendingTag());
    setTagInput("");
  };
  const save = () => {
    if (readOnly || loading || visionPending === "analyze" || !name.trim())
      return;
    const savedTags = canTag ? withPendingTag() : tags;
    setTags(savedTags);
    setTagInput("");
    dispatch(
      updateCollectionAsset(
        liveAsset,
        null,
        name.trim(),
        description,
        undefined,
        asset.source?.name,
        asset.source?.notes,
        undefined,
        true,
        canTag ? changesForTags(savedTags) : { add: [], remove: [] }
      )
    );
  };
  const downloadUrl = convertCustomerUploadsS3ToCloudFront(
    liveAsset.downloadUrl || liveAsset.storage?.url || ""
  );
  const previewUrl = convertCustomerUploadsS3ToCloudFront(
    liveAsset.meta?.isImage && !checkIfHeic(liveAsset.meta?.mime)
      ? downloadUrl || liveAsset.thumbnail
      : liveAsset.thumbnail || ""
  );
  const usedInDraft = Boolean(liveAsset.usedInDraftPosts?.length);
  const usedInUpcoming = Boolean(liveAsset.usedInUpcomingPosts?.length);
  const sourceUrl = asset.source?.value;
  const sourceIsLink = sourceUrl && /^https?:\/\//i.test(sourceUrl);
  const metadataSize = Number(liveAsset.meta?.size);
  const format = liveAsset.meta?.format;
  const fileSize =
    metadataSize > 0
      ? metadataSize
      : Number(typeof format === "object" ? format?.size : undefined);

  return (
    <>
      <Sheet
        open
        modal={false}
        onOpenChange={(open) => !open && requestLeave()}
      >
        <SheetContent
          side="right"
          hideOverlay
          showCloseButton={false}
          aria-describedby={undefined}
          // The context menu restores focus to its trigger after Details opens.
          // Keep that focus restoration from dismissing this nonmodal sheet.
          onFocusOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            // Let a Library card click request a guarded switch instead of
            // dismissing this nonmodal sheet on pointerdown before click runs.
            if (
              loading ||
              editorOpen ||
              pendingAction ||
              (event.target instanceof Element &&
                event.target.closest(
                  "[data-library-asset-details], [data-library-details-menu], [data-library-card-actions]"
                ))
            )
              event.preventDefault();
          }}
          onEscapeKeyDown={(event) => {
            if (loading || editorOpen || pendingAction) event.preventDefault();
          }}
          className="inset-y-auto top-[var(--library-details-top,64px)] right-[var(--library-details-right,8px)] bottom-[var(--library-details-bottom,8px)] flex h-auto w-[722px] max-w-[calc(100vw-40px)] flex-col gap-0 overflow-visible border-neutral-100 p-0 shadow-none [filter:drop-shadow(0px_12px_8px_rgba(16,24,40,0.08))_drop-shadow(0px_4px_3px_rgba(16,24,40,0.03))] sm:max-w-[calc(100vw-40px)]"
        >
          <div className="relative flex h-14 shrink-0 items-center justify-between bg-blue-50 px-4">
            <button
              type="button"
              aria-label="Close preview"
              disabled={loading}
              onClick={() => requestLeave()}
              className="absolute -left-8 top-4 flex size-8 items-center justify-center rounded-l-lg border-0 bg-white p-0 text-neutral-800 hover:bg-blue-25 focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <i className="fa-regular fa-xmark text-base" aria-hidden />
            </button>
            <SheetTitle className="font-gabarito text-xl font-bold leading-6 text-neutral-800">
              Details
            </SheetTitle>
            <div className="flex gap-2">
              <Button
                variant="pink-primary"
                size="icon-sm"
                aria-label="Create post"
                disabled={loading}
                className="rounded-lg shadow-[0_1px_2px_rgba(16,24,40,0.05)] [&_svg]:size-5"
                onClick={(event) =>
                  requestLeave(() => {
                    close();
                    card.handleCreatePost(event);
                  })
                }
              >
                <CreatePostIcon />
              </Button>
              <div className="flex">
                {!readOnly &&
                  card.isEditable &&
                  entitlementsPermission.hasEditorLibrary(user) && (
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label="Edit"
                      disabled={loading}
                      className="rounded-l-lg rounded-r-none"
                      onClick={() =>
                        requestLeave(() => {
                          setName(asset.name ?? "");
                          setDescription(asset.description ?? "");
                          setTagChanges({ add: [], remove: [] });
                          setTagInput("");
                          card.handleEdit();
                        })
                      }
                    >
                      <i
                        className="fa-regular fa-pen-to-square text-base"
                        aria-hidden
                      />
                    </Button>
                  )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label="Asset actions"
                      disabled={loading}
                      className="rounded-l-none rounded-r-lg border-l-0"
                    >
                      <i
                        className="fa-regular fa-ellipsis text-base"
                        aria-hidden
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    data-library-details-menu
                    align="end"
                    size="sm"
                    className="w-40"
                  >
                    <DropdownMenuItem
                      size="sm"
                      disabled={readOnly}
                      onSelect={() =>
                        requestLeave(() => {
                          close();
                          card.handleMove();
                        })
                      }
                    >
                      <i
                        className="fa-regular fa-arrows-up-down-left-right w-5 text-base"
                        aria-hidden
                      />
                      Move
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      size="sm"
                      onSelect={() =>
                        requestLeave(() => {
                          close();
                          card.handleCopy();
                        })
                      }
                    >
                      <i
                        className="fa-regular fa-clone w-5 text-base"
                        aria-hidden
                      />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      size="sm"
                      disabled={readOnly}
                      variant="destructive"
                      onSelect={() =>
                        requestLeave(() => {
                          close();
                          card.handleDelete();
                        })
                      }
                    >
                      <i
                        className="fa-regular fa-trash-can w-5 text-base"
                        aria-hidden
                      />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,402fr)_minmax(0,320fr)] bg-white font-inter text-sm leading-5 text-neutral-800 max-sm:flex max-sm:flex-col max-sm:overflow-y-auto">
            <div className="min-w-0 overflow-y-auto border-x border-neutral-100 max-sm:overflow-visible">
              <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-neutral-50">
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 size-full scale-110 object-cover blur-[20px]"
                  />
                )}
                {liveAsset.meta?.isVideo ? (
                  <video
                    key={downloadUrl}
                    controls
                    preload="metadata"
                    poster={liveAsset.thumbnail}
                    src={downloadUrl}
                    aria-label="Video preview"
                    className="relative size-full object-contain"
                  />
                ) : previewUrl ? (
                  <a
                    href={downloadUrl || previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative flex size-full items-center justify-center"
                  >
                    <img
                      src={previewUrl}
                      alt={liveAsset.name || "Preview"}
                      data-testid="assetPreview"
                      className="size-full object-contain object-center"
                    />
                  </a>
                ) : (
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative p-8 text-blue-500"
                  >
                    Open media
                  </a>
                )}
              </div>
              <section className="space-y-1.5 p-4" aria-label="AI Vision">
                <h3 className={labelClass}>
                  AI vision{" "}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="About AI vision"
                        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-sm p-0 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <i className="fa-regular fa-circle-info" aria-hidden />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      AI-generated descriptions and tags help you find this
                      asset. They’re separate from your editable description.
                    </TooltipContent>
                  </Tooltip>
                </h3>
                <p className="whitespace-pre-wrap text-sm font-normal leading-5">
                  {liveAsset.visionDescription ||
                    (vision?.status === "complete"
                      ? liveAsset.meta?.isVideo
                        ? "Analysis is not available for this video."
                        : "Analysis complete. No description was returned."
                      : vision?.status === "skipped-too-long"
                        ? `Analysis skipped: videos must be ${vision.maxVideoDurationSeconds} seconds or shorter.`
                        : vision?.status === "failed" || (visionError && vision)
                          ? liveAsset.meta?.isVideo
                            ? "Analysis is not available for this video."
                            : "Analysis failed. Please try again."
                          : "Not analyzed yet")}
                </p>
                {visionError && !vision && (
                  <p role="alert" className="text-sm font-normal leading-5">
                    Unable to load analysis information.
                  </p>
                )}
                {!readOnly &&
                  !liveAsset.visionDescription &&
                  !asset.visionDescription && (
                    <>
                      {visionPending === "load" && (
                        <p
                          role="status"
                          className="text-sm font-normal leading-5"
                        >
                          Loading analysis information...
                        </p>
                      )}
                      {visionError && !vision && (
                        <Button
                          variant="outline"
                          disabled={Boolean(visionPending)}
                          onClick={reload}
                        >
                          Retry loading analysis
                        </Button>
                      )}
                      {vision?.canAnalyze &&
                        vision.status !== "complete" &&
                        vision.status !== "skipped-too-long" && (
                          <Button
                            variant="outline"
                            disabled={Boolean(visionPending) || loading}
                            onClick={analyze}
                          >
                            {visionPending === "analyze" ? (
                              <>
                                <i
                                  className="fa-regular fa-spinner fa-spin mr-2"
                                  aria-hidden
                                />
                                Analyzing...
                              </>
                            ) : vision.status === "failed" || visionError ? (
                              "Retry analysis"
                            ) : (
                              "Analyze"
                            )}
                          </Button>
                        )}
                    </>
                  )}
              </section>
            </div>
            <div className="min-w-0 space-y-4 overflow-y-auto p-4 max-sm:overflow-visible">
              <div className="space-y-1.5">
                <label htmlFor="asset-filename" className={labelClass}>
                  File name
                </label>
                <Input
                  id="asset-filename"
                  value={name}
                  readOnly={readOnly}
                  disabled={loading}
                  onChange={(event) => setName(event.target.value)}
                  className="h-8 rounded-lg border-neutral-100 px-3 py-1.5 text-sm shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="asset-description" className={labelClass}>
                  Description
                </label>
                <Textarea
                  id="asset-description"
                  value={description}
                  readOnly={readOnly}
                  disabled={loading}
                  onChange={(event) => setDescription(event.target.value)}
                  className="h-[136px] min-h-[136px] resize-none rounded-lg border-neutral-100 px-3.5 py-2 text-sm shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                />
              </div>
              <section className="space-y-1.5" aria-label="Tags">
                <label htmlFor="asset-tag" className={labelClass}>
                  Tags
                </label>
                {canTag ? (
                  <div className="flex gap-2">
                    <Input
                      id="asset-tag"
                      value={tagInput}
                      disabled={loading}
                      placeholder="Enter a tag"
                      className="h-8 min-w-0 rounded-lg border-neutral-100 px-3 text-sm shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                      onChange={(event) => setTagInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (
                          event.key === "Enter" &&
                          !event.nativeEvent.isComposing
                        ) {
                          event.preventDefault();
                          addTag();
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-[52px] shrink-0 rounded-lg shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                      disabled={loading || !tagInput.trim()}
                      onClick={addTag}
                    >
                      Add
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-neutral-500">
                    {readOnly
                      ? "Tags are managed at the source."
                      : "Upgrade your library to edit tags."}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-0.5">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex max-w-full items-center gap-1 rounded bg-neutral-25 px-1.5 py-0.5 text-xs font-medium leading-4"
                    >
                      <span className="break-all">{tag}</span>
                      {canTag && (
                        <button
                          type="button"
                          disabled={loading}
                          aria-label={`Remove tag ${tag}`}
                          onClick={() =>
                            setTags(tags.filter((item) => item !== tag))
                          }
                          className="flex size-[15px] shrink-0 items-center justify-center p-0"
                        >
                          <i
                            className="fa-regular fa-xmark text-xs"
                            aria-hidden
                          />
                        </button>
                      )}
                    </span>
                  ))}
                  {tags.length === 0 && (
                    <span className="text-neutral-500">No tags</span>
                  )}
                </div>
              </section>
              <div className="-mx-4 grid grid-cols-2 gap-4 border-t border-neutral-100 px-4 pt-4">
                <div className="min-w-0 space-y-1.5">
                  <h3 className={labelClass}>Source</h3>
                  {sourceIsLink ? (
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-words text-blue-500"
                    >
                      {asset.source?.name || "View source"}
                    </a>
                  ) : (
                    <p className="text-sm font-normal leading-5">
                      {asset.source?.name || "—"}
                    </p>
                  )}
                  {asset.source?.notes && (
                    <p className="whitespace-pre-wrap text-xs text-neutral-500">
                      {asset.source.notes}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <h3 className={labelClass}>Dimensions</h3>
                  <p className="text-sm font-normal leading-5">
                    {liveAsset.meta?.width && liveAsset.meta?.height
                      ? `${liveAsset.meta.width} × ${liveAsset.meta.height}`
                      : "—"}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <h3 className={labelClass}>Size</h3>
                  <p className="text-sm font-normal leading-5">
                    {Number.isFinite(fileSize) && fileSize > 0
                      ? bytes(fileSize, { decimalPlaces: 0 })
                      : "—"}
                  </p>
                </div>
                <section aria-label="Usage" className="space-y-1.5">
                  <h3 className={labelClass}>Usage</h3>
                  {usedInDraft && (
                    <p className="text-sm font-normal leading-5">
                      Used in a draft
                    </p>
                  )}
                  {usedInUpcoming && (
                    <p className="text-sm font-normal leading-5">
                      Used in an upcoming post
                    </p>
                  )}
                  {!usedInDraft && !usedInUpcoming && (
                    <p className="text-sm font-normal leading-5">
                      Not used in drafts or upcoming posts
                    </p>
                  )}
                </section>
              </div>
              <ContentCollectProvenancePanel
                asset={liveAsset}
                showSourceNotes={false}
              />
            </div>
          </div>
          <div className="flex h-16 shrink-0 items-center justify-end gap-2 rounded-br-xl border-l border-t border-neutral-100 bg-white px-4">
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
              disabled={loading}
              onClick={() => requestLeave()}
            >
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button
                variant="blue-primary"
                size="sm"
                className="rounded-lg shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
                disabled={
                  loading || visionPending === "analyze" || !name.trim()
                }
                onClick={save}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog
        open={Boolean(pendingAction)}
        onOpenChange={(open) => !open && keepEditing()}
      >
        <AlertDialogContent>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            Your changes to this asset have not been saved.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                const action = pendingAction;
                setPendingAction(null);
                action?.();
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Suspense fallback={null}>
        {card.showPhotoEditor && (
          <CreativePhotoEditorModal
            show
            image={card.photoEditorImageUrl}
            assetId={card.photoEditorAssetId}
            assetUrl={card.photoEditorAssetUrl}
            isReplacingOriginal={card.photoEditorIsReplacingOriginal}
            onClose={card.handlePhotoEditorClose}
            onSave={async (blob) => {
              if (await card.handlePhotoEditorSave(blob)) close();
            }}
            progress={card.photoEditorProgress}
            saveButtonText={card.photoEditorSaveButtonText}
            message={card.photoEditorMessage}
            isSaving={card.photoEditorIsSaving}
          />
        )}
        {card.showVideoEditor && (
          <CreativeVideoEditorModal
            show
            video={card.videoEditorVideoUrl}
            assetId={card.videoEditorAssetId}
            assetUrl={card.videoEditorAssetUrl}
            isReplacingOriginal={card.videoEditorIsReplacingOriginal}
            onClose={card.handleVideoEditorClose}
            onSave={async (blob) => {
              if (await card.handleVideoEditorSave(blob)) close();
            }}
            progress={card.videoEditorProgress}
            saveButtonText={card.videoEditorSaveButtonText}
            message={card.videoEditorMessage}
            isSaving={card.videoEditorIsSaving}
          />
        )}
      </Suspense>
    </>
  );
}
